import React, { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { Instagram, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const COLS = [
  { title: "Aprender", links: [["Aprender alemán", "/aprender-aleman"], ["Cursos", "/cursos"], ["Prueba de nivel", "/prueba-de-nivel"], ["Herramientas", "/herramientas"], ["Glosario", "/glosario"]] },
  { title: "Trabajar y vivir", links: [["Alemán para trabajar", "/aleman-para-trabajar"], ["Vivir en Suiza", "/vivir-en-suiza"], ["Guías", "/guias"]] },
  { title: "Hallo Suiza", links: [["Quiénes somos", "/quienes-somos"], ["Equipo editorial", "/autores/equipo-editorial"], ["Metodología editorial", "/metodologia-editorial"], ["Política de correcciones", "/politica-de-correcciones"], ["Contacto", "/contacto"], ["Preguntas frecuentes", "/preguntas-frecuentes"]] },
  { title: "Legal", links: [["Aviso legal", "/aviso-legal"], ["Privacidad", "/privacidad"], ["Cookies", "/cookies"], ["Términos", "/terminos"]] },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Debes aceptar el consentimiento."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/newsletter", { email, consent });
      toast.success(data.message);
      setEmail(""); setConsent(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <footer className="border-t border-border bg-card mt-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo />
            <p className="mt-4 text-sm text-muted-foreground max-w-xs leading-relaxed">
              El alemán que necesitas para vivir y trabajar en Suiza. Lecciones breves, situaciones reales y explicaciones en español.
            </p>
            <form onSubmit={subscribe} className="mt-6 max-w-sm" data-testid="newsletter-form">
              <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-2">Newsletter</label>
              <div className="flex gap-2">
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com" data-testid="newsletter-email"
                  className="flex-1 h-10 rounded-md border border-input bg-background px-3 text-sm" />
                <button disabled={sending} data-testid="newsletter-submit"
                  className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60">
                  Suscribirme
                </button>
              </div>
              <label className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5" data-testid="newsletter-consent" />
                <span>Acepto recibir correos de Hallo Suiza. Puedo darme de baja cuando quiera.</span>
              </label>
            </form>
          </div>
          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-8">
            {COLS.map((c) => (
              <div key={c.title}>
                <h4 className="font-display font-semibold text-sm mb-3">{c.title}</h4>
                <ul className="space-y-2">
                  {c.links.map(([l, to]) => (
                    <li key={to}>
                      <Link to={to} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{l}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Hallo Suiza · Contenido educativo, no constituye asesoramiento legal, fiscal, migratorio ni médico.
          </p>
          <div className="flex items-center gap-3">
            <a href="https://instagram.com/hallo.suiza" target="_blank" rel="noopener noreferrer"
              className="p-2 rounded-md hover:bg-secondary transition-colors" aria-label="Instagram @hallo.suiza" data-testid="instagram-link">
              <Instagram size={18} />
            </a>
            <Link to="/contacto" className="p-2 rounded-md hover:bg-secondary transition-colors" aria-label="Contacto">
              <Mail size={18} />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
